import { observable, action, runInAction } from 'mobx'
import { clearMemoized } from '@/utils/memoize-util'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { type GetCurrentUserFn } from '@/features/iam'
import { type WorkspaceStore } from './WorkspaceStore'
import { type MemberStore } from '@/features/iam/members/MemberStore'
import { type IamApi } from '@/features/iam/api/iam-api'
import type { Membership } from '@/features/iam/workspaces/Membership'
import { Id } from '@/infra/id'
import { mapMembershipResourceToMembership } from './workspace-mappers'
import { type MembershipResource } from '@/features/iam/api/resources/workspaces'
import type { ListWorkspaceMembershipsOptions } from '@/features/iam'

export class WorkspaceMembershipStore {
  /**
   * The current user's workspace memberships.
   */
  @observable accessor memberships: Membership[] = []

  constructor(
    private readonly _workspaceStore: WorkspaceStore,
    private readonly _memberStore: MemberStore,
    private readonly _api: IamApi,
    private readonly _getCurrentUser: GetCurrentUserFn,
  ) {}

  /**
   * Lists the current user's workspace memberships.
   */
  async listWorkspaceMemberships(
    options: ListWorkspaceMembershipsOptions = {},
    signal?: AbortSignal,
  ): Promise<Membership[]> {
    if (options.skipCache) {
      clearMemoized(this._listWorkspaceMemberships)
    }

    return this._listWorkspaceMemberships(signal)
  }

  /**
   * Gets the current user's already-listed workspace memberships.
   */
  getCurrentUserWorkspaceMemberships(): Membership[] {
    return this.memberships.slice()
  }

  @action.bound
  addMembership(membership: Membership) {
    this.memberships.push(membership)
  }

  /**
   * Fetches a user's workspace memberships.
   */
  async listUserWorkspaceMemberships(
    params: ListUserWorkspaceMembershipsParams,
    signal?: AbortSignal,
  ) {
    const memberships = await this._api.users.getUserWorkspaceMemberships(
      Id.assert(params.userPublicId),
      { inactive: params.inactive },
      signal,
    )
    return memberships.map(this._mapMembershipResourceToMembership)
  }

  // Keyed on nothing, so calls with and without a signal share the fetch.
  @memoizeAsync({ keySelector: () => '' })
  private async _listWorkspaceMemberships(
    signal?: AbortSignal,
  ): Promise<Membership[]> {
    const memberships = await this._api.users.getUserWorkspaceMemberships(
      Id.assert(this._getCurrentUser().publicId),
      undefined,
      signal,
    )
    return runInAction(() => {
      const mapped = memberships.map(this._mapMembershipResourceToMembership)
      this.memberships = mapped

      return mapped.slice()
    })
  }

  private readonly _mapMembershipResourceToMembership = (
    resource: MembershipResource,
  ): Membership =>
    mapMembershipResourceToMembership(
      resource,
      (props) => this._workspaceStore.primeFullWorkspace(props),
      (member) => this._memberStore.primeFullMember(member),
    )
}

/**
 * Params for {@link WorkspaceMembershipStore.listUserWorkspaceMemberships}.
 */
export interface ListUserWorkspaceMembershipsParams {
  userPublicId: Id
  inactive?: boolean
}
